import React from 'react';
import clsx from 'clsx';
import { List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { makeTransparent } from '../../util/colors';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const useStyles = makeStyles(theme => ({
    listItem: {
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
    },
    link: {
        borderRadius: 4,
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
    },
    linkActive: {
        backgroundColor: `${makeTransparent(theme.palette.primary.main, 0.06)} !important`,
        position: 'relative',
        '&::after': {
            content: '""',
            display: 'block',
            width: 5,
            height: 5,
            background: theme.palette.primary.main,
            borderRadius: 5,
            position: 'absolute',
            right: theme.spacing(2),
            top: '50%',
            transform: 'translateY(-50%)',
        },
    },
}));

interface CommonNavigationItemProps {
    icon: React.ReactElement;
    /**
     * Indent the text even if there is no icon given
     */
    inset?: boolean;
    children: React.ReactNode;
}

export interface NavigationItemProps extends CommonNavigationItemProps {
    path: string;
    /**
     * Only highlight the node as active, if the exact path is active, i.e.
     * not on a sub-page
     */
    exact?: boolean;
}

/**
 * @param {NavigationItemProps} props
 */
export const NavigationItem: React.FunctionComponent<NavigationItemProps> = (props) => {
    const {
        path,
        icon: IconComponent,
        children: label,
        exact = false,
        inset = false,
    } = props;

    const location = useLocation();
    const currentPath = location.pathname;
    const active = currentPath === path;
    const activeRootline = currentPath.startsWith(path);
    const highlight = exact && active || activeRootline;
    const classes = useStyles();

    return (
        <li className={classes.listItem}>
            <ListItem
                button
                className={clsx(classes.link, highlight && classes.linkActive)}
                component={RouterLink}
                to={path}
                selected={activeRootline}
                role={null}
            >
                {IconComponent && (
                    <ListItemIcon>
                        <IconComponent color={highlight ? 'primary' : 'inherit'} />
                    </ListItemIcon>
                )}
                <ListItemText
                    primaryTypographyProps={{ color: highlight ? 'primary' : 'inherit' }}
                    inset={!IconComponent && inset}
                >
                    {label}
                </ListItemText>
            </ListItem>
        </li>
    );
};

export interface ExternalNavigationItemProps extends CommonNavigationItemProps {
    url: string;
    linkProps?: React.HTMLProps<HTMLAnchorElement>;
}

 /**
 * @param {ExternalNavigationItemProps} props
 */
export const ExternalNavigationItem: React.FunctionComponent<ExternalNavigationItemProps> = (props) => {
    const {
        url,
        icon: IconComponent,
        children: label,
        inset = false,
        linkProps = {},
    } = props;

    const classes = useStyles();

    return (
        <li className={classes.listItem}>
            <ListItem button className={clsx(classes.link)} component="a" target="_blank" href={url} role={null} {...linkProps}>
                {IconComponent && (
                    <ListItemIcon>
                        <IconComponent/>
                    </ListItemIcon>
                )}
                <ListItemText inset={!IconComponent && inset}>{label}</ListItemText>
                <OpenInNewIcon color="action"/>
            </ListItem>
        </li>
    );
};

export interface MenuItem {
    path: string;
    label: string;
    icon?: React.ReactNode;
    exact?: boolean;
    inset?: string;
}

export interface NavigationProps {
    items: MenuItem[];
}

/**
 * @param {NavigationProps} props
 */
export const Navigation: React.FunctionComponent<NavigationProps> = ({ items }) => {
    return (
        <List>
            {items.map((item, i) => (
                <NavigationItem
                    key={i}
                    path={item.path}
                    icon={item.icon}
                    exact={item.exact}
                >
                    {item.label}
                </NavigationItem>
            ))}
        </List>
    );
};
